/**
 * What Ovid asks of a buyer, as the intake says it (plan `2026_10_09_ovid_first_offer.md`): the plan, and one more muse
 * for a subscriber. What a buyer is charged is the Stripe price, not these constants: its amount is set in terraform
 * (`deployment/environments/prod/eu_central_1/stripe.tf`, `local.stripe_ovid_catalog`). The price's base is the dollar
 * and carries a fixed `eur` option of the same amount, so the figure reads the same in both currencies.
 */
import {CURRENCY_SYMBOL, type DisplayCurrency} from '@core/billing/displayCurrency'

/**
 * The broad line (PROPOSITION.md § 1) on every surface: the home, its share card, the default meta of every page, the
 * proof page's hero. The proof page's approved section copy (`proof/spec.ts`), clean of the explicit lexicon
 * (`lexicon.ts`) and of any age mention (PROPOSITION.md § 2, Terms).
 */
export const HERO_TITLE = 'Pictures for every fantasy'
export const FRONT_TITLE = `Ovid — ${HERO_TITLE.toLowerCase()}`
export const CATALOGUE_LINE = 'Thousands of pictures, sorted by fantasy.'
export const WOMAN_LINE = 'The same woman in every picture: choose one, or make your own from photos.'
export const EDIT_LINE = 'Change any picture with a few words.'
export const FRONT_LINE = [CATALOGUE_LINE, WOMAN_LINE, EDIT_LINE].join(' ')

export const PLAN_PRICE_UNITS = 24
export const MUSE_PRICE_UNITS = 15

/** Pictures per rolling day, by whether a plan is in force: what the offer promises and what
 *  `wall/allowance.server.ts` holds the account to. Both numbers live here and nowhere else. */
export const FREE_ALLOWANCE = 9
export const PLAN_ALLOWANCE = 300

const price = (units: number, currency: DisplayCurrency) => `${CURRENCY_SYMBOL[currency]}${units}`

/** The plan's price as the wall's bar says it, beside its door. */
export const planPerMonth = (currency: DisplayCurrency = 'usd') => `${price(PLAN_PRICE_UNITS, currency)} / month`

/** What the intake's Start will ask to be paid, said under the title (`startControl`'s `asks`). */
export function intakePriceLine(asks: 'plan' | 'muse' | null, currency: DisplayCurrency = 'usd'): string {
    if (asks === 'plan') {
        // True whether or not a muse of hers already holds the plan's one: a lapsed subscriber pays the plan, then the
        // extra muse, and the line has said so.
        return `Ovid plan: ${price(PLAN_PRICE_UNITS, currency)} a month. It includes one muse of your own and many more pictures each day. More muses: ${price(MUSE_PRICE_UNITS, currency)} each. Cancel any time.`
    }
    if (asks === 'muse') {
        return `One more muse: ${price(MUSE_PRICE_UNITS, currency)}, paid once. She is yours to keep.`
    }
    return 'Nothing to pay.'
}
